import { defineStore } from 'pinia';
import { Container } from 'typescript-ioc';
import User from '../entity/User';
import UserService from '../service/UserService';
import Storage from '../Storage';
import Constant from '../config/Constant';
import Notification from '../Notification';
import { getRouter } from '../util/GlobalRouter';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null as User | null,
        token: null as string | null,
        _hydrated: false,
    }),
    getters: {
        isLoggedIn: (state) => {
            return !!(state.token && typeof state.token === 'string' && state.token.length > 10);
        },
        currentUser: (state) => state.user,
    },
    actions: {
        /**
         * Hydrate state from cookies - call this after app init
         * This ensures Storage Vue VM is ready before accessing cookies
         */
        hydrate() {
            if (this._hydrated) return;

            try {
                const userData = Storage.getCookie().get('user');
                if (userData && typeof userData === 'object') {
                    this.user = userData as User;
                    this.token = (userData as any).token || null;
                }
            } catch (e) {
                console.warn('[AuthStore] Failed to hydrate from cookies:', e);
            }

            this._hydrated = true;
        },
        setUser(user: User) {
            this.user = user;
            if (user.token) {
                this.token = user.token;
            }
        },
        setToken(token: string) {
            this.token = token;
        },
        clearAuth() {
            this.user = null;
            this.token = null;
        },
        async login(entity: User): Promise<any> {
            const userService = Container.get(UserService);
            const { LOGIN_SUCCESS, LOGIN_FAILED } = Constant.STATUS.API;

            try {
                const response = await userService.login(entity);

                if (response.isSuccess) {
                    const data = response.getData({});

                    Notification.showNotification({ status: LOGIN_SUCCESS, type: 'success' });

                    // Persistence
                    if (data.user && data.user.merchant) {
                        Storage.getLocalStorage().set('merchant', data.user.merchant);
                    }

                    const createData = Object.assign({}, data.user, { token: data.access_token || '' });

                    // Update State
                    this.setUser(createData);

                    // Update Cookies (Legacy Support & Persistence)
                    Storage.getCookie().set('user', createData);

                    // Redirect to home after successful login
                    getRouter().push('/index');
                } else {
                    Notification.showNotification({ status: LOGIN_FAILED }, true);
                }

                return response;
            } catch (error) {
                Notification.showNotification({ status: LOGIN_FAILED }, true);
                return { isSuccess: false };
            }
        },
        logout() {
            Storage.getLocalStorage().remove('merchant');

            // Clear State
            this.clearAuth();

            // Clear Storage
            Storage.getCookie().remove('user');
            Storage.getCookie().remove();
            Storage.getLocalStorage().remove();

            Notification.showNotification({ text: 'Mohon Tunggu ...', duration: 1500 });

            // Redirect (Optional here, usually handled by component or router)
            window.location.reload();
        }
    }
});
